import {
  Alert,
  Box,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useAuthedApi } from "../../api/useAuthedApi";
import type { PendingPublisher, PublisherSelf, Submission } from "../../api/publisherApi";
import { type AuthUser } from "../../auth/useAuth";
import { WorkspaceCard } from "../../components/Common/WorkspaceCard";
import { generateKeypair, downloadPrivateKey, isEd25519Available } from "../../lib/ed25519";
import { readPluginFolder } from "../../lib/pluginFolder";
import { packageAndSign } from "../../lib/pluginPackage";

const STAFF_ROLES = ["plugin_admin", "admin"];

function message(e: unknown): string {
  return e instanceof Error && e.message ? e.message : "Something went wrong";
}

function submissionBadge(
  state: string,
): { label: string; color: "default" | "success" | "error" | "warning" } {
  switch (state) {
    case "submitted":
    case "in_review":
      return { label: "Pending review", color: "default" };
    case "listed":
      return { label: "Live", color: "success" };
    case "rejected":
      return { label: "Rejected", color: "error" };
    case "quarantined":
      return { label: "Quarantined", color: "warning" };
    case "yanked":
      return { label: "Yanked", color: "default" };
    default:
      return { label: state, color: "default" };
  }
}

/** The publisher onboarding content. Exported bare for unit tests; the route
 *  renders it inside the shared WorkspaceLayout (Publisher.tsx) so it gets
 *  the sidebar + inset padding like every other workspace page. */
export function PublisherPage() {
  const api = useAuthedApi();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [self, setSelf] = useState<PublisherSelf | null | undefined>(undefined);
  const [pending, setPending] = useState<PendingPublisher[] | null>(null);
  const [form, setForm] = useState({
    slug: "",
    display_name: "",
    contact_email: "",
    homepage_url: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [keys, setKeys] = useState<string[]>([]);
  const [keyBusy, setKeyBusy] = useState(false);
  const [keyNotice, setKeyNotice] = useState<string | null>(null);
  const [keyError, setKeyError] = useState<string | null>(null);
  const [pubBusy, setPubBusy] = useState(false);
  const [pubResult, setPubResult] = useState<string | null>(null);
  const [pubError, setPubError] = useState<string | null>(null);
  const [folderFiles, setFolderFiles] = useState<File[]>([]);
  const [keyJwk, setKeyJwk] = useState<JsonWebKey | null>(null);
  const [submissions, setSubmissions] = useState<Submission[] | null>(null);

  const isStaff = !!user?.roles?.some((r) => STAFF_ROLES.includes(r));
  const verified = !!self?.verified;
  const slug = self?.slug;

  const loadSelf = useCallback(() => {
    void api.publisher.getSelf().then(setSelf);
  }, [api]);

  const loadKeys = useCallback(() => {
    if (!slug) return;
    api.publisher.listKeys(slug).then(setKeys).catch(() => setKeys([]));
  }, [api, slug]);

  const loadPending = useCallback(() => {
    // UI gate is cosmetic — the proxy + backend enforce staff-only access.
    // Defense in depth: hide the section entirely if the call 403s.
    api.publisher.listPending().then(setPending).catch(() => setPending(null));
  }, [api]);

  const loadSubmissions = useCallback(() => {
    if (!slug) return;
    api.publisher.listSubmissions(slug).then(setSubmissions).catch(() => setSubmissions(null));
  }, [api, slug]);

  // Routed through `useAuthedApi` so it works under both cookie SPA + embed
  // bearer flows. Re-fetches on tab focus so out-of-band permission changes
  // reach the gates without a full page reload.
  useEffect(() => {
    let cancelled = false;
    const refresh = () => {
      api.auth.me().then(
        (u) => { if (!cancelled) setUser(u); },
        () => { if (!cancelled) setUser(null); },
      );
    };
    refresh();
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
    };
  }, [api]);
  useEffect(() => {
    loadSelf();
  }, [loadSelf]);
  useEffect(() => {
    if (isStaff) loadPending();
  }, [isStaff, loadPending]);
  useEffect(() => {
    if (verified) loadKeys();
  }, [verified, loadKeys]);
  useEffect(() => {
    if (verified) loadSubmissions();
  }, [verified, loadSubmissions]);

  async function onApply() {
    setError(null);
    setSubmitting(true);
    try {
      await api.publisher.apply(form);
      loadSelf();
    } catch (e: any) {
      setError(e?.status === 409 ? "You already have a publisher." : message(e));
    } finally {
      setSubmitting(false);
    }
  }

  async function onVerify(slug: string) {
    await api.publisher.verify(slug);
    loadPending();
  }

  async function onGenerate() {
    if (!slug) return;
    setKeyBusy(true);
    setKeyError(null);
    setKeyNotice(null);
    try {
      const { publicKeyB64, privateJwk } = await generateKeypair();
      const { key_fingerprint } = await api.publisher.registerKey(slug, publicKeyB64);
      // Only download AFTER a successful register — no orphan key file.
      downloadPrivateKey(slug, key_fingerprint, privateJwk);
      setKeyNotice(
        "Your private key was downloaded. This is the only copy — save it now. It was not stored.",
      );
      loadKeys();
    } catch (e) {
      setKeyError(message(e));
    } finally {
      setKeyBusy(false);
    }
  }

  async function onRevoke(fingerprint: string) {
    if (!slug) return;
    try {
      await api.publisher.revokeKey(slug, fingerprint);
    } finally {
      loadKeys();
    }
  }

  async function onKeyFile(file: File) {
    try {
      setKeyJwk(JSON.parse(await file.text()));
      setPubError(null);
    } catch {
      setPubError("invalid signing key file");
      setKeyJwk(null);
    }
  }

  async function onPublish() {
    if (!slug || folderFiles.length === 0 || !keyJwk) return;
    setPubBusy(true);
    setPubError(null);
    setPubResult(null);
    try {
      const { manifest, files } = await readPluginFolder(folderFiles);
      const { tarballBytes, signatureB64 } = await packageAndSign({
        manifest,
        files,
        privateJwk: keyJwk,
      });
      await api.publisher.publish(tarballBytes, signatureB64);
      setPubResult("Submitted for review.");
      loadSubmissions();
    } catch (e: any) {
      setPubError(e?.detail ?? e?.message ?? "Publish failed.");
    } finally {
      setPubBusy(false);
    }
  }

  return (
    <>
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Publisher
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          Publish plugins to the marketplace under your company's name.
        </Typography>
      </Box>

      {self === undefined ? (
        <Typography color="text.secondary">Loading…</Typography>
      ) : self === null ? (
        <WorkspaceCard interactive={false}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Become a publisher
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Stack spacing={2} sx={{ maxWidth: 420 }}>
            <TextField
              label="Slug"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
            />
            <TextField
              label="Display name"
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
            />
            <TextField
              label="Contact email"
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
            />
            <TextField
              label="Homepage (optional)"
              value={form.homepage_url}
              onChange={(e) => setForm({ ...form, homepage_url: e.target.value })}
            />
            <Box>
              <Button variant="contained" onClick={onApply} disabled={submitting}>
                Apply
              </Button>
            </Box>
          </Stack>
        </WorkspaceCard>
      ) : (
        <WorkspaceCard interactive={false}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography variant="h6">{self.display_name}</Typography>
            <Typography variant="body2" color="text.secondary">
              ({self.slug})
            </Typography>
            {self.suspended ? (
              <Chip label="Suspended" color="error" size="small" />
            ) : self.verified ? (
              <Chip label="Verified" color="success" size="small" />
            ) : (
              <Chip label="Awaiting verification" color="warning" size="small" />
            )}
          </Box>
        </WorkspaceCard>
      )}

      {verified && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Signing keys
          </Typography>
          <WorkspaceCard interactive={false}>
            {!isEd25519Available() ? (
              <Alert severity="warning">
                Your browser does not support Ed25519 key generation.
              </Alert>
            ) : (
              <Box>
                <Button variant="contained" onClick={onGenerate} disabled={keyBusy}>
                  {keyBusy ? "Generating…" : "Generate new key"}
                </Button>
              </Box>
            )}
            {keyNotice && (
              <Alert severity="success" role="status" sx={{ mt: 2 }} onClose={() => setKeyNotice(null)}>
                {keyNotice}
              </Alert>
            )}
            {keyError && (
              <Alert severity="error" sx={{ mt: 2 }} onClose={() => setKeyError(null)}>
                {keyError}
              </Alert>
            )}
            {keys.length === 0 ? (
              <Typography color="text.secondary" sx={{ mt: 2 }}>
                No active signing keys.
              </Typography>
            ) : (
              <List dense sx={{ mt: 1 }}>
                {keys.map((fp) => (
                  <ListItem
                    key={fp}
                    secondaryAction={
                      <Button size="small" color="error" onClick={() => onRevoke(fp)}>
                        Revoke
                      </Button>
                    }
                  >
                    <ListItemText primary={<code>{fp}</code>} />
                  </ListItem>
                ))}
              </List>
            )}
          </WorkspaceCard>
        </Box>
      )}

      {verified && keys.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Publish a plugin
          </Typography>
          <WorkspaceCard interactive={false}>
            <Stack spacing={2} sx={{ maxWidth: 480 }}>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Plugin folder
                </Typography>
                <input
                  type="file"
                  // @ts-expect-error webkitdirectory is non-standard but widely supported
                  webkitdirectory=""
                  multiple
                  onChange={(e) => setFolderFiles(Array.from(e.target.files ?? []))}
                />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Signing key (.json)
                </Typography>
                <input
                  type="file"
                  accept=".json,application/json"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void onKeyFile(f);
                  }}
                />
              </Box>
              <Box>
                <Button
                  variant="contained"
                  onClick={onPublish}
                  disabled={pubBusy || folderFiles.length === 0 || !keyJwk}
                >
                  {pubBusy ? "Publishing…" : "Publish"}
                </Button>
              </Box>
              {pubResult && (
                <Alert severity="success" role="status">
                  {pubResult}
                </Alert>
              )}
              {pubError && <Alert severity="error">{pubError}</Alert>}
            </Stack>
          </WorkspaceCard>
        </Box>
      )}

      {verified && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            My submissions
          </Typography>
          <WorkspaceCard interactive={false}>
            {submissions === null || submissions.length === 0 ? (
              <Typography color="text.secondary">No submissions yet.</Typography>
            ) : (
              <List dense>
                {submissions.map((s) => {
                  const badge = submissionBadge(s.listing_state);
                  const showReason =
                    (s.listing_state === "rejected" || s.listing_state === "quarantined") &&
                    !!s.quarantine_reason;
                  return (
                    <ListItem key={`${s.name}@${s.version}`} alignItems="flex-start">
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography component="span" variant="body2">
                              <strong>{s.name}</strong>@{s.version}
                            </Typography>
                            <Chip label={badge.label} color={badge.color} size="small" />
                          </Box>
                        }
                        secondary={
                          <Box component="span" sx={{ display: "block" }}>
                            {showReason && (
                              <Box component="span" sx={{ display: "block" }}>
                                {s.quarantine_reason}
                              </Box>
                            )}
                            {s.reviewed_at && (
                              <Box
                                component="span"
                                sx={{ display: "block", fontSize: "0.75rem", color: "text.secondary" }}
                              >
                                Reviewed {s.reviewed_at}
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </WorkspaceCard>
        </Box>
      )}

      {isStaff && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Pending verification
          </Typography>
          {pending === null || pending.length === 0 ? (
            <Typography color="text.secondary">None.</Typography>
          ) : (
            <Stack spacing={1.5}>
              {pending.map((p) => (
                <WorkspaceCard key={p.slug} interactive={false}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2">
                      {p.display_name} ({p.slug}) — {p.contact_email}
                    </Typography>
                    <Button size="small" onClick={() => onVerify(p.slug)}>
                      Verify
                    </Button>
                  </Box>
                </WorkspaceCard>
              ))}
            </Stack>
          )}
        </Box>
      )}
    </>
  );
}
